'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Carousel } from '@zillow/constellation-10';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { FloorPlanCard } from './FloorPlanCard';
import { FloorPlanDots } from './FloorPlanDots';
import { useFloorPlanCarousel } from './hooks/useFloorPlanCarousel';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

type DesktopFloorPlanCarouselProps = {
  floorplans: Array<Floorplan>;
  listing: PropertyDetailListing;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  onSeeAll: (floorplan: Floorplan) => void;
};

export const DesktopFloorPlanCarousel = ({
  floorplans,
  listing,
  onUnitSelect,
  onSeeAll,
}: DesktopFloorPlanCarouselProps) => {
  const t = useTranslations('hdp');
  const { carousel, currentSlide, visibleCount, goToSlide } = useFloorPlanCarousel();

  const positions = Math.max(1, floorplans.length - (visibleCount ?? floorplans.length) + 1);
  const showControls = positions > 1;
  // At the end of the rail the nearest slide can be past the last position.
  const currentPosition = Math.min(currentSlide, positions - 1);

  return (
    <div>
      <div className={styles.desktopCarouselRow}>
        <div className={styles.desktopArrowColumn}>
          {showControls && (
            <button
              type="button"
              className={styles.desktopControlButton}
              onClick={() => goToSlide(currentPosition - 1)}
              disabled={currentPosition === 0}
              aria-label={t('previousFloorPlans')}
            >
              <LucideIcon icon={ChevronLeft} size="sm" />
            </button>
          )}
        </div>

        <div className={styles.desktopCardsArea}>
          <Carousel.ScrollContainer
            ref={carousel.scrollContainer}
            onScroll={carousel.handleOnScroll}
            onKeyUp={carousel.scrollToFocusedEl}
            slideGap="md"
            aria-label={t('floorPlans')}
          >
            {floorplans.map((fp, index) => (
              <Carousel.Slide
                key={fp.modelId}
                ref={index === 0 ? carousel.firstSlide : undefined}
                slideAlign="start"
                className={styles.desktopCarouselItem}
              >
                <FloorPlanCard
                  floorplan={fp}
                  listing={listing}
                  onUnitSelect={onUnitSelect}
                  onSeeAll={onSeeAll}
                  compact
                />
              </Carousel.Slide>
            ))}
          </Carousel.ScrollContainer>
        </div>

        <div className={styles.desktopArrowColumn}>
          {showControls && (
            <button
              type="button"
              className={styles.desktopControlButton}
              onClick={() => goToSlide(currentPosition + 1)}
              disabled={currentPosition === positions - 1}
              aria-label={t('nextFloorPlans')}
            >
              <LucideIcon icon={ChevronRight} size="sm" />
            </button>
          )}
        </div>
      </div>

      {showControls && <FloorPlanDots count={positions} current={currentPosition} onSelect={goToSlide} />}
    </div>
  );
};
